import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '@hooks';
import { BRANCH_KEY } from '@constants/automations';
import { NODE_HEIGHT, V_GAP } from './layout';

const CORNER = 12;

const orthogonalPath = points => {
    let d = `M ${points[0][0]},${points[0][1]}`;
    for (let i = 1; i < points.length - 1; i += 1) {
        const [prevX, prevY] = points[i - 1];
        const [x, y] = points[i];
        const [nextX, nextY] = points[i + 1];
        const r = Math.min(CORNER, Math.hypot(x - prevX, y - prevY) / 2, Math.hypot(nextX - x, nextY - y) / 2);
        d += ` L ${x - Math.sign(x - prevX) * r},${y - Math.sign(y - prevY) * r}`;
        d += ` Q ${x},${y} ${x + Math.sign(nextX - x) * r},${y + Math.sign(nextY - y) * r}`;
    }
    const [lastX, lastY] = points[points.length - 1];
    return `${d} L ${lastX},${lastY}`;
};

const routeEdge = ({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, laneX }) => {
    const joinY = targetY - V_GAP / 2;

    if (laneX != null) {
        const outY = sourceY + V_GAP / 2;
        return [
            orthogonalPath([
                [sourceX, sourceY],
                [sourceX, outY],
                [laneX, outY],
                [laneX, joinY],
                [targetX, joinY],
                [targetX, targetY],
            ]),
            laneX,
            (outY + joinY) / 2,
        ];
    }

    const bent = sourceX !== targetX;
    const [path, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        borderRadius: CORNER,
        centerY: bent ? joinY : undefined,
    });
    if (bent && joinY - sourceY > NODE_HEIGHT) return [path, sourceX, (sourceY + joinY) / 2];
    return [path, labelX, labelY];
};

const InsertEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) => {
    const { t } = useTranslation();
    const [path, labelX, labelY] = routeEdge({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        laneX: data?.laneX,
    });

    const branchLabel =
        data?.branchKey === BRANCH_KEY.YES
            ? t('AutomationsPage.editor.branchYes')
            : data?.branchKey === BRANCH_KEY.NO
              ? t('AutomationsPage.editor.branchNo')
              : null;

    return (
        <>
            <BaseEdge id={id} path={path} style={{ stroke: 'var(--divider)', strokeWidth: 1.5 }} />
            <EdgeLabelRenderer>
                {data?.onInsert && (
                    <button
                        type="button"
                        onClick={() => data.onInsert(id)}
                        title={t('AutomationsPage.editor.addStep')}
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: '#fff',
                            border: '1px solid var(--divider)',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            pointerEvents: 'all',
                            padding: 0,
                        }}
                    >
                        <AddIcon style={{ fontSize: 15 }} />
                    </button>
                )}
                {branchLabel && (
                    <div
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${sourceX}px, ${sourceY + 18}px)`,
                            background: '#fff',
                            border: '1px solid var(--divider)',
                            borderRadius: 6,
                            padding: '1px 6px',
                            fontSize: 11,
                            color: 'var(--text-secondary)',
                            fontFamily: 'Rubik, sans-serif',
                            pointerEvents: 'none',
                        }}
                    >
                        {branchLabel}
                    </div>
                )}
            </EdgeLabelRenderer>
        </>
    );
};

export default InsertEdge;
